import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import ClassIcon from '@mui/icons-material/Class';
import FunctionsIcon from '@mui/icons-material/Functions';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import {
  getCoveragePackages,
  getCoverageClasses,
  getCoverageMethods,
  getCoverageTestsForMethod,
  searchCoverage,
  getCoverageModuleStructure,
} from '../../services/apiService';
const WINDOW_PRESETS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 180, label: '180d' },
  { value: 365, label: '1y' },
];
const DEFAULT_WINDOW_DAYS = 30;

const coveragePct = (covered, total) => {
  if (!total) return 0;
  return Math.round((covered / total) * 1000) / 10;
};

const CoverageChip = ({ covered, total }) => {
  const pct = coveragePct(covered, total);
  let color = 'default';
  if (total > 0) {
    if (pct >= 75) color = 'success';
    else if (pct >= 40) color = 'warning';
    else color = 'error';
  }
  return (
    <Chip
      size="small"
      label={`${covered}/${total} (${pct}%)`}
      color={color}
      variant="outlined"
      sx={{ ml: 1 }}
    />
  );
};

const methodSig = (m) =>
  `${m.methodName}${m.methodParams || ''}${m.returnType ? `: ${m.returnType}` : ''}`;

/**
 * Flat table view of every method in a class. Replaces the older
 * expand-each-method-then-expand-tests accordion. The tests cell is a button
 * — clicking it opens a Dialog that lazy-loads and renders the test list
 * for that method as its own table.
 */
const MethodsTable = ({ methods, buildId, windowDays }) => {
  const [dialog, setDialog] = useState(null); // { method, tests, loading, err }

  const openTests = async (method) => {
    setDialog({ method, tests: null, loading: true, err: null });
    try {
      const resp = await getCoverageTestsForMethod({
        methodId: method.methodId,
        buildId,
        windowDays,
      });
      setDialog((d) =>
        d && d.method.methodId === method.methodId
          ? { ...d, tests: resp?.data?.tests || [], loading: false }
          : d
      );
    } catch (e) {
      setDialog((d) =>
        d && d.method.methodId === method.methodId
          ? { ...d, loading: false, err: e.message }
          : d
      );
    }
  };

  const closeTests = () => setDialog(null);

  return (
    <Box sx={{ pl: 8, pr: 2, py: 1 }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ width: 'fit-content', maxWidth: '100%' }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: 'auto',
            '& td, & th': { py: 0.5, px: 1.25 },
            '& tbody tr:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <colgroup>
            <col style={{ width: 540 }} />
            <col style={{ width: 80 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 110 }} />
          </colgroup>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Probes</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Covered</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Tests</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {methods.map((m) => (
              <TableRow key={m.methodId}>
                <TableCell
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={methodSig(m)}
                >
                  <FunctionsIcon
                    fontSize="inherit"
                    sx={{ mr: 0.5, color: 'text.secondary', verticalAlign: 'middle' }}
                  />
                  {methodSig(m)}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                  {m.probesCount}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={m.covered ? 'covered' : 'not covered'}
                    color={m.covered ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ height: 22 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant={m.testCount ? 'outlined' : 'text'}
                    disabled={!m.testCount}
                    onClick={() => openTests(m)}
                    sx={{
                      minWidth: 90,
                      py: 0,
                      px: 1,
                      textTransform: 'none',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.testCount} {m.testCount === 1 ? 'test' : 'tests'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!dialog}
        onClose={closeTests}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <ScienceIcon color="primary" sx={{ mt: 0.5 }} />
            <Box flex={1} minWidth={0}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                Tests covering this method
              </Typography>
              {dialog && (
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', wordBreak: 'break-all', mt: 0.5 }}
                >
                  {methodSig(dialog.method)}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={closeTests}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dialog && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {dialog.tests
                  ? `${dialog.tests.length} ${dialog.tests.length === 1 ? 'test' : 'tests'} touched this method's signature in the last ${windowDays} days · sorted most recent first.`
                  : `Loading tests from the last ${windowDays} days…`}
              </Typography>
              {dialog.loading && (
                <Box display="flex" alignItems="center" gap={1} py={2}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading tests…</Typography>
                </Box>
              )}
              {dialog.err && <Alert severity="error">{dialog.err}</Alert>}
              {dialog.tests && dialog.tests.length === 0 && (
                <Alert severity="info">No registered tests cover this method in the selected window.</Alert>
              )}
              {dialog.tests && dialog.tests.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" stickyHeader sx={{ '& td, & th': { py: 0.5, px: 1.25 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Test</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 140 }}>Last seen</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, width: 80 }}>Hits</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dialog.tests.map((t) => (
                        <TableRow key={t.testDefinitionId} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {t.testName}
                            {t.testRunner && (
                              <Chip
                                size="small"
                                label={t.testRunner}
                                sx={{ ml: 1, height: 20 }}
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                            {t.lastSeenDay ? String(t.lastSeenDay).slice(0, 10) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                            {t.hitCount ?? '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTests}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ClassNode = ({ klass, buildId, windowDays, includeSynthetic, autoOpen, focusMethodId }) => {
  const [open, setOpen] = useState(!!autoOpen);
  const [methods, setMethods] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await getCoverageMethods({
        classFqn: klass.classFqn,
        buildId,
        windowDays,
        includeSynthetic,
      });
      setMethods(resp?.data?.methods || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && methods === null && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = () => setOpen(!open);

  return (
    <Box>
      <ListItemButton onClick={toggle} sx={{ pl: 5 }}>
        {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        <ClassIcon fontSize="small" sx={{ mx: 1, color: 'primary.main' }} />
        <ListItemText
          primary={
            <Box display="flex" alignItems="center">
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {klass.className}
              </Typography>
              <Chip size="small" label={`module: ${klass.module || 'No module'}`} sx={{ ml: 1 }} />
              <CoverageChip covered={klass.coveredMethodCount} total={klass.methodCount} />
            </Box>
          }
          secondary={
            <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
              {klass.classFqn}
            </Typography>
          }
        />
      </ListItemButton>
      <Collapse in={open} unmountOnExit>
        {loading && (
          <Box sx={{ pl: 8, py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        )}
        {err && (
          <Box sx={{ pl: 8, py: 1 }}>
            <Alert severity="error">{err}</Alert>
          </Box>
        )}
        {methods && methods.length > 0 && (
          <MethodsTable methods={methods} buildId={buildId} windowDays={windowDays} />
        )}
        {methods && methods.length === 0 && (
          <Box sx={{ pl: 8, py: 1 }}>
            <Typography variant="caption" color="textSecondary">
              No methods in this class for the current build.
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

const PackageNode = ({ pkg, buildId, windowDays, includeSynthetic, autoOpen, focusClassFqn, focusMethodId }) => {
  const [open, setOpen] = useState(!!autoOpen);
  const [classes, setClasses] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await getCoverageClasses({
        packageName: pkg.packageName,
        buildId,
        windowDays,
        includeSynthetic,
      });
      setClasses(resp?.data?.classes || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && classes === null && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Box>
      <ListItemButton onClick={() => setOpen(!open)} sx={{ pl: 2 }}>
        {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        <FolderIcon sx={{ mx: 1, color: 'warning.dark' }} />
        <ListItemText
          primary={
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                {pkg.packageName}
              </Typography>
              <Chip size="small" label={`module: ${pkg.module || 'No module'}`} sx={{ ml: 1 }} />
              <Chip size="small" label={`${pkg.classCount} classes`} sx={{ ml: 1 }} />
              <CoverageChip covered={pkg.coveredMethodCount} total={pkg.methodCount} />
            </Box>
          }
        />
      </ListItemButton>
      <Collapse in={open} unmountOnExit>
        {loading && (
          <Box sx={{ pl: 5, py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        )}
        {err && (
          <Box sx={{ pl: 5, py: 1 }}>
            <Alert severity="error">{err}</Alert>
          </Box>
        )}
        {classes &&
          classes.map((c) => (
            <ClassNode
              key={c.classFqn}
              klass={c}
              buildId={buildId}
              windowDays={windowDays}
              includeSynthetic={includeSynthetic}
              autoOpen={focusClassFqn === c.classFqn}
              focusMethodId={focusClassFqn === c.classFqn ? focusMethodId : null}
            />
          ))}
        {classes && classes.length === 0 && (
          <Box sx={{ pl: 5, py: 1 }}>
            <Typography variant="caption" color="textSecondary">
              No classes in this package.
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

const CoverageTreeView = ({ onBackToLanding, initialWindowDays = DEFAULT_WINDOW_DAYS, initialFilter = null }) => {
  const navigate = useNavigate();
  const goLanding = () => {
    if (typeof onBackToLanding === 'function') onBackToLanding();
    else navigate('/coverage');
  };
  const [packages, setPackages] = useState([]);
  const [buildId, setBuildId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [windowDays, setWindowDays] = useState(initialWindowDays);
  const [includeSynthetic, setIncludeSynthetic] = useState(false);
  // { module: string } or { section: string } — restricts visible packages to
  // the rows the user clicked on the landing page. Cleared via the chip.
  const [filter, setFilter] = useState(initialFilter);
  const [moduleMapOpen, setModuleMapOpen] = useState(false);
  const [moduleMap, setModuleMap] = useState(null);
  const [moduleMapLoading, setModuleMapLoading] = useState(false);
  const [moduleMapErr, setModuleMapErr] = useState(null);

  const openModuleMap = async () => {
    setModuleMapOpen(true);
    if (moduleMap || moduleMapLoading) return;
    setModuleMapLoading(true);
    setModuleMapErr(null);
    try {
      const resp = await getCoverageModuleStructure();
      setModuleMap(resp?.data?.sections || []);
    } catch (e) {
      setModuleMapErr(e.message);
    } finally {
      setModuleMapLoading(false);
    }
  };

  const [searchQ, setSearchQ] = useState('');
  const [searchHits, setSearchHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const [focus, setFocus] = useState({ packageName: null, classFqn: null, methodId: null });

  const loadPackages = async (w = windowDays, synth = includeSynthetic) => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await getCoveragePackages({ windowDays: w, includeSynthetic: synth });
      setPackages(resp?.data?.packages || []);
      setBuildId(resp?.data?.buildId || null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPackages(windowDays, includeSynthetic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays, includeSynthetic]);

  // Debounced search
  useEffect(() => {
    if (!searchQ || !searchQ.trim()) {
      setSearchHits(null);
      return undefined;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const resp = await searchCoverage({ q: searchQ, windowDays, includeSynthetic });
        setSearchHits(resp?.data?.results || []);
      } catch (e) {
        setErr(e.message);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [searchQ, windowDays, includeSynthetic]);

  const filteredPackages = useMemo(() => {
    let pkgs = packages;
    if (filter?.module) pkgs = pkgs.filter((p) => p.module === filter.module);
    if (filter?.section) pkgs = pkgs.filter((p) => p.section === filter.section);
    if (searchQ && searchHits) {
      const pkgSet = new Set(searchHits.map((h) => h.packageName));
      pkgs = pkgs.filter((p) => pkgSet.has(p.packageName));
    }
    return pkgs;
  }, [packages, searchHits, searchQ, filter]);

  const handleHitClick = (hit) => {
    setFocus({
      packageName: hit.packageName,
      classFqn: hit.classFqn,
      methodId: hit.methodId,
    });
    setSearchQ('');
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={2} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Box>
          <Breadcrumbs>
            <MuiLink
              component="button"
              onClick={goLanding}
              underline="hover"
            >
              Coverage
            </MuiLink>
            <Typography color="text.primary">Tree</Typography>
          </Breadcrumbs>
          <Typography variant="h4" gutterBottom sx={{ mt: 1 }}>
            Coverage Tree
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Packages → classes → methods → registered tests. Build:{' '}
            <code>{buildId || '(none found)'}</code> · Window:{' '}
            <strong>last {windowDays} days</strong>
            {!includeSynthetic && ' · synthetic methods hidden'}
          </Typography>
          {filter && (filter.module || filter.section) && (
            <Box sx={{ mt: 1 }}>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={
                  filter.module
                    ? `Filtered to module: ${filter.module}`
                    : `Filtered to area: ${filter.section}`
                }
                onDelete={() => setFilter(null)}
              />
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Tooltip title="Coverage window — a method is 'covered' if any test, on any build, touched it within this period. See Drill4J-Coverage-Methodology.md for full rules.">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Window
              </Typography>
            </Stack>
          </Tooltip>
          <ToggleButtonGroup
            value={windowDays}
            exclusive
            size="small"
            onChange={(_, v) => v && setWindowDays(v)}
          >
            {WINDOW_PRESETS.map((p) => (
              <ToggleButton key={p.value} value={p.value} sx={{ px: 1.2, py: 0.4 }}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Show JVM-synthesised methods (lambda$…, access$…, <clinit>). Off by default so coverage reflects only hand-written code.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeSynthetic}
                  onChange={(e) => setIncludeSynthetic(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Show synthetic
                </Typography>
              }
              sx={{ ml: 0.5, mr: 0 }}
            />
          </Tooltip>
          <Tooltip title="See how com/os packages are grouped into modules (parsed from docs/coverage/module-structure.md)">
            <Button
              size="small"
              variant="outlined"
              startIcon={<AccountTreeIcon />}
              onClick={openModuleMap}
            >
              View Module Map
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      <Dialog
        open={moduleMapOpen}
        onClose={() => setModuleMapOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Module Map</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Parsed from <code>docs/coverage/module-structure.md</code>. Coverage rows are labelled by
            longest-prefix match against this taxonomy; anything that does not match
            shows up as <strong>No module</strong>.
          </Typography>
          {moduleMapLoading && <CircularProgress size={20} />}
          {moduleMapErr && <Alert severity="error">{moduleMapErr}</Alert>}
          {moduleMap && moduleMap.length === 0 && !moduleMapLoading && (
            <Alert severity="warning">
              No module mapping found. Check that <code>docs/coverage/module-structure.md</code> exists on the server.
            </Alert>
          )}
          {moduleMap &&
            moduleMap.map((s) => (
              <Box key={s.section} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {s.section}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Path</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Submodule / Category</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {s.rows.map((r) => (
                        <TableRow key={r.path}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.path}</TableCell>
                          <TableCell>{r.module}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{r.submodule}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleMapOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search any package, class, or method (e.g. UserSyncDB, dashboard, save)"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              </InputAdornment>
            ),
          }}
        />

        {searchQ && searchHits && (
          <Box sx={{ mt: 1, maxHeight: 240, overflow: 'auto' }}>
            {searchHits.length === 0 ? (
              <Typography variant="caption" color="textSecondary">
                No matches.
              </Typography>
            ) : (
              <List dense disablePadding>
                {searchHits.map((h) => (
                  <ListItemButton
                    key={h.methodId}
                    onClick={() => handleHitClick(h)}
                    sx={{ fontFamily: 'monospace' }}
                  >
                    <ListItemText
                      primary={`${h.classFqn} · ${h.methodName}${h.methodParams || ''}`}
                      primaryTypographyProps={{
                        sx: { fontFamily: 'monospace', fontSize: 13 },
                      }}
                      secondary={h.packageName}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        )}
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}
      {err && <Alert severity="error">{err}</Alert>}

      {!loading && !err && (
        <Paper sx={{ p: 1 }}>
          {filteredPackages.length === 0 && (
            <Box p={3}>
              <Typography color="textSecondary">No packages found for this build.</Typography>
            </Box>
          )}
          <List key={`tree-${windowDays}-${includeSynthetic ? 'syn' : 'real'}`} dense disablePadding>
            {filteredPackages.map((p) => (
              <React.Fragment key={p.packageName}>
                <PackageNode
                  pkg={p}
                  buildId={buildId}
                  windowDays={windowDays}
                  includeSynthetic={includeSynthetic}
                  autoOpen={focus.packageName === p.packageName}
                  focusClassFqn={focus.packageName === p.packageName ? focus.classFqn : null}
                  focusMethodId={focus.packageName === p.packageName ? focus.methodId : null}
                />
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default CoverageTreeView;
